import { useState, FormEvent } from 'react';
import { pressInquiryTypes } from '@/mocks/press';

const CONTACT_EMAIL = 'damola@oludamolaadebowale.com';

type Status = 'idle' | 'sent';

export default function PressContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const message = data.get('message') as string;
    if (message && message.length > 500) return;

    const name = data.get('name') as string;
    const email = data.get('email') as string;
    const organisation = data.get('organisation') as string;
    const enquiryType = data.get('enquiry_type') as string;

    const subject = encodeURIComponent(`[${enquiryType}] Enquiry from ${name}`);
    const body = encodeURIComponent(
      `Name: ${name}\nEmail: ${email}${organisation ? `\nOrganisation: ${organisation}` : ''}\nEnquiry Type: ${enquiryType}\n\nMessage:\n${message}`
    );

    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    setStatus('sent');
    form.reset();
    setCharCount(0);
  };

  return (
    <section className="bg-white py-20" id="contact">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left — info */}
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">GET IN TOUCH</p>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Press &amp; Enquiries
            </h2>
            <p className="font-sans text-base text-gray-600 leading-relaxed mb-8">
              For interview requests, exhibition collaborations, academic partnerships, speaking engagements, or general enquiries, please use the form. All messages are reviewed personally.
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#f9f6f1] text-[#b89a6a] shrink-0">
                  <i className="ri-mail-line text-lg" />
                </div>
                <div>
                  <p className="font-sans text-sm font-semibold text-gray-900 mb-0.5">Email</p>
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="font-sans text-sm text-[#b89a6a] hover:underline cursor-pointer"
                  >
                    {CONTACT_EMAIL}
                  </a>
                  <p className="font-sans text-xs text-gray-400 mt-0.5">Responded within 48 hours</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#f9f6f1] text-[#b89a6a] shrink-0">
                  <i className="ri-newspaper-line text-lg" />
                </div>
                <div>
                  <p className="font-sans text-sm font-semibold text-gray-900 mb-0.5">Press Enquiries</p>
                  <p className="font-sans text-sm text-gray-500">Interview requests, features, and media coverage</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#f9f6f1] text-[#b89a6a] shrink-0">
                  <i className="ri-building-line text-lg" />
                </div>
                <div>
                  <p className="font-sans text-sm font-semibold text-gray-900 mb-0.5">Institutional Partnerships</p>
                  <p className="font-sans text-sm text-gray-500">Museums, universities, and cultural organisations</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div>
            {status === 'sent' ? (
              <div className="bg-[#f9f6f1] rounded-lg p-10 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#b89a6a]/20 text-[#b89a6a] mx-auto mb-4">
                  <i className="ri-mail-send-line text-2xl" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-gray-900 mb-2">Email Client Opened</h3>
                <p className="font-sans text-sm text-gray-600 mb-4">
                  Your message has been prepared and your email client should have opened. Simply hit send to reach Oludamola directly.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="font-sans text-sm text-[#b89a6a] hover:underline cursor-pointer"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form
                id="press-enquiry-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-sans text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                      Full Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Your full name"
                      className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm font-sans text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#b89a6a] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                      Email <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm font-sans text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#b89a6a] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-sans text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                    Organisation / Publication
                  </label>
                  <input
                    type="text"
                    name="organisation"
                    placeholder="BBC, University of Lagos, etc."
                    className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm font-sans text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#b89a6a] transition-colors"
                  />
                </div>

                <div>
                  <label className="block font-sans text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                    Enquiry Type <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="enquiry_type"
                    required
                    className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm font-sans text-gray-900 focus:outline-none focus:border-[#b89a6a] transition-colors bg-white cursor-pointer"
                  >
                    <option value="">Select enquiry type</option>
                    {pressInquiryTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-sans text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                    Message <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Please describe your enquiry in detail..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm font-sans text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#b89a6a] transition-colors resize-none"
                  />
                  <p className={`text-xs font-sans mt-1 text-right ${charCount > 480 ? 'text-red-400' : 'text-gray-400'}`}>
                    {charCount}/500
                  </p>
                </div>

                <p className="font-sans text-xs text-gray-400">
                  Clicking &ldquo;Send Enquiry&rdquo; will open your email client addressed to{' '}
                  <span className="text-[#b89a6a]">{CONTACT_EMAIL}</span> with your message pre-filled.
                </p>

                <button
                  type="submit"
                  className="w-full bg-gray-900 text-white font-sans text-sm font-medium py-3 rounded-md hover:bg-[#b89a6a] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Send Enquiry
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}


